import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

/**
 * UserAuthModal — grants/revokes `functions` for a single user.
 *
 * Two-column dual list: left column shows every catalogue function NOT
 * currently granted ("Available"), right column shows the ones currently
 * granted ("Assigned"). Clicking a row (or its arrow button) moves it to
 * the other column; nothing is persisted until Save.
 */
export default function UserAuthModal({ user, canWrite = true, currentUserId, isSuperClient = false, onClose }) {
  const [functions, setFunctions] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [initial, setInitial] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // A user can't change their own function grants (self-privilege-escalation
  // guard) — except SUPER-client admins, who may edit anyone including themselves.
  const isSelf = !isSuperClient && user.user_id === currentUserId;

  useEffect(() => {
    let ignore = false;
    setErr("");
    Promise.all([api.listFunctions(), api.getUserAuth(user.user_id)])
      .then(([allFns, grantsResp]) => {
        if (ignore) return;
        const list = allFns || [];
        const current = new Set((grantsResp?.functions || []).map((g) => g.function_id));
        setFunctions(list);
        setSelected(new Set(current));
        setInitial(new Set(current));
      })
      .catch((e) => {
        if (!ignore) setErr(e?.response?.data?.detail || e.message || "Failed to load");
      });
    return () => { ignore = true; };
  }, [user.user_id]);

  const assign = (fid) => {
    if (!canWrite) return;
    setSelected((prev) => new Set(prev).add(fid));
  };
  const revoke = (fid) => {
    if (!canWrite) return;
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(fid);
      return next;
    });
  };

  const { available, assigned } = useMemo(() => {
    const all = functions || [];
    return {
      available: all.filter((f) => !selected.has(f.function_id)),
      assigned: all.filter((f) => selected.has(f.function_id)),
    };
  }, [functions, selected]);

  const dirty = (() => {
    if (selected.size !== initial.size) return true;
    for (const v of selected) if (!initial.has(v)) return true;
    return false;
  })();

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = Array.from(selected);
      await api.setUserAuth(user.user_id, payload);
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.user_id;

  const colStyle = {
    display: "flex",
    flexDirection: "column",
    border: "1px solid var(--nyse-line, #ddd)",
    borderRadius: 6,
    minHeight: 320,
    maxHeight: 760,
    overflow: "hidden",
  };
  const colHeadStyle = {
    padding: "8px 12px",
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    borderBottom: "1px solid var(--nyse-line, #ddd)",
    background: "var(--panel-soft, #f7f8fa)",
  };
  const colBodyStyle = {
    flex: 1,
    overflowY: "auto",
    padding: 4,
    // Canonical light-blue data-list backdrop used everywhere else in the app.
    background: "#e6f0fb",
  };
  const rowStyle = (interactive) => ({
    display: "flex", alignItems: "center", gap: 10,
    padding: "7px 8px", borderRadius: 4,
    cursor: interactive ? "pointer" : "default",
  });

  const FnRow = ({ f, direction, onMove }) => (
    <div
      key={f.function_id}
      data-testid={`userauth-modal-row-${f.function_id}`}
      style={rowStyle(canWrite)}
      onClick={() => canWrite && onMove(f.function_id)}
      className="userauth-dual-row"
    >
      {direction === "assign" && (
        <button
          type="button"
          data-testid={`userauth-modal-assign-${f.function_id}`}
          disabled={!canWrite || busy}
          onClick={(e) => { e.stopPropagation(); onMove(f.function_id); }}
          className="btn-ghost btn-sm"
          style={{ padding: "2px 8px", flexShrink: 0 }}
          title="Assign"
        >→</button>
      )}
      <span style={{ fontFamily: "var(--font-mono, monospace)", minWidth: 100 }}>
        {f.function_id}
      </span>
      <span className="muted" style={{ flex: 1, fontSize: 13 }}>
        {f.function_desc || ""}
      </span>
      {direction === "revoke" && (
        <button
          type="button"
          data-testid={`userauth-modal-revoke-${f.function_id}`}
          disabled={!canWrite || busy}
          onClick={(e) => { e.stopPropagation(); onMove(f.function_id); }}
          className="btn-ghost btn-sm"
          style={{ padding: "2px 8px", flexShrink: 0 }}
          title="Revoke"
        >←</button>
      )}
    </div>
  );

  return (
    <div
      className="modal-backdrop"
      data-testid="userauth-modal-backdrop"
      onClick={onClose}
    >
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 1230 }}
        data-testid="userauth-modal-dialog"
      >
        <div className="modal-head">
          <h2>
            User Auth — {fullName}{" "}
            <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>
              ({user.user_id})
            </span>
          </h2>
          <button
            type="button"
            className="modal-close"
            aria-label="Close"
            data-testid="userauth-modal-close"
            onClick={onClose}
          >×</button>
        </div>
        <form className="modal-form" onSubmit={save}>
          {!canWrite && (
            <div className="muted" data-testid="userauth-modal-readonly"
                 style={{ marginBottom: 8, fontSize: 12 }}>
              View only — you don't have permission to change grants.
            </div>
          )}
          {canWrite && isSelf && (
            <div className="muted" data-testid="userauth-modal-self-hint"
                 style={{ marginBottom: 8, fontSize: 12 }}>
              You can't change your own function grants — ask another admin.
            </div>
          )}
          {!functions ? (
            <div className="loading-row" style={{ padding: "20px 0" }}>
              <span className="spinner" /> Loading functions…
            </div>
          ) : functions.length === 0 ? (
            <div className="muted" data-testid="userauth-modal-empty"
                 style={{ padding: "12px 0 4px" }}>
              No functions defined yet. Add some on the Functions page first.
            </div>
          ) : (
            <div
              data-testid="userauth-modal-fns"
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
            >
              <div style={colStyle}>
                <div style={colHeadStyle} data-testid="userauth-modal-available-head">
                  Available ({available.length})
                </div>
                <div style={colBodyStyle} data-testid="userauth-modal-available">
                  {available.length === 0 ? (
                    <div className="muted" style={{ padding: 10, fontSize: 13 }}>
                      Every function is assigned.
                    </div>
                  ) : (
                    available.map((f) => (
                      <FnRow key={f.function_id} f={f} direction="assign" onMove={assign} />
                    ))
                  )}
                </div>
              </div>
              <div style={colStyle}>
                <div style={colHeadStyle} data-testid="userauth-modal-assigned-head">
                  Assigned ({assigned.length})
                </div>
                <div style={colBodyStyle} data-testid="userauth-modal-assigned">
                  {assigned.length === 0 ? (
                    <div className="muted" style={{ padding: 10, fontSize: 13 }}>
                      No functions granted yet.
                    </div>
                  ) : (
                    assigned.map((f) => (
                      <FnRow key={f.function_id} f={f} direction="revoke" onMove={revoke} />
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {err && (
            <div className="form-error" data-testid="userauth-modal-error">{err}</div>
          )}

          <div
            className="modal-actions no-divider"
            style={{ justifyContent: "flex-end", gap: 10 }}
          >
            <button
              type="button"
              className="btn-ghost"
              data-testid="userauth-modal-cancel"
              onClick={onClose}
              disabled={busy}
              style={{ padding: "10px 18px" }}
            >Close</button>
            {canWrite && (
              <button
                type="submit"
                className="btn-primary"
                data-testid="userauth-modal-save"
                disabled={busy || !dirty || !functions || isSelf}
                title={isSelf ? "You can't change your own function grants" : ""}
                style={{ width: "auto", padding: "10px 22px", margin: 0 }}
              >{busy ? "Saving…" : "Save"}</button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
